/**
 * Sound Intent — the public, meaning-based audio API for MeesterJay.
 *
 * App-Feel 2027 §3A / §5.2 — "Geluid Heeft Eén Logica" (Sound Intent SSOT).
 *
 * Rationale: callers used to pick a low-level synth function directly
 * (`soundManager.playStreak` vs `playAchievement` vs `playLevelUp`), so the
 * "which sound belongs to which kind of moment" knowledge lived in scattered
 * heads + one archived matrix audit — not in code. That is drift waiting to
 * happen, and it hung sound on UI *events* (every hover, every tap) instead of
 * on *meaning*.
 *
 * This layer is the single source of truth for that mapping. Callers express an
 * INTENT (what kind of moment this is); the layer decides the synth function
 * AND applies per-category cadans so a category can't rattle:
 *
 *   - `mascot.speak`  → longer debounce: Djay's voice never machine-guns on
 *                       menu-hover / rapid taps (the §3A "Djay praat te vaak"
 *                       root cause).
 *   - `reward.*` / `answer.correct` → short de-dupe window so two celebration
 *                       sounds fired for the SAME feedback moment collapse to
 *                       one (e.g. the onboarding double playLevelUp+playCorrect).
 *   - `nav.*` / `counter.tick` → pass-through (soundManager already throttles
 *                       these per-sound at 80ms).
 *
 * `soundManager` remains the low-level Web-Audio synth. This is the layer the
 * rest of the app should import going forward.
 */

import { soundManager } from '../soundManager'

// ── Intent taxonomy (meaning, not synth) ─────────────────────
export type SoundIntent =
  | 'answer.correct'
  | 'answer.wrong'
  | 'reward.levelUp'
  | 'reward.streak'
  | 'reward.streakMilestone'
  | 'reward.achievement'
  | 'reward.goalReached'
  | 'nav.move'
  | 'nav.click'
  | 'mascot.speak'
  | 'counter.tick'
  | 'counter.sweep'
  | 'system.block'

export interface SoundIntentOptions {
  /** Consecutive-correct streak — passed through to the combo-pitch chime. */
  comboLevel?: number
  /**
   * For `counter.sweep` — the run length (ms) of the counting animation so the
   * audio sweep finishes in sync with the visual count. App-Feel 2027 W11.
   */
  durationMs?: number
}

// ── Cadans categories ────────────────────────────────────────
// Each intent maps to a cadans bucket with a minimum interval. A play call is
// dropped if the SAME bucket fired more recently than its window. soundManager
// keeps its own 80ms per-sound guard underneath; this layer adds the
// meaning-level rhythm on top.
type CadansBucket = 'speak' | 'reward' | 'passthrough'

const CADANS_WINDOW_MS: Record<CadansBucket, number> = {
  // Djay's voice: long enough to kill the hover/rapid-tap stream, short enough
  // that a deliberate, spaced-out interaction still bloops.
  speak: 900,
  // One celebration per feedback moment — collapses accidental stacking.
  reward: 250,
  // soundManager already guards these; no extra gate here.
  passthrough: 0,
}

const INTENT_BUCKET: Record<SoundIntent, CadansBucket> = {
  'answer.correct': 'reward',
  'answer.wrong': 'reward',
  'reward.levelUp': 'reward',
  'reward.streak': 'reward',
  'reward.streakMilestone': 'reward',
  'reward.achievement': 'reward',
  'reward.goalReached': 'reward',
  'nav.move': 'passthrough',
  'nav.click': 'passthrough',
  'mascot.speak': 'speak',
  'counter.tick': 'passthrough',
  'counter.sweep': 'passthrough',
  'system.block': 'passthrough',
}

const lastBucketAt = new Map<CadansBucket, number>()

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

function passesCadans(bucket: CadansBucket): boolean {
  const window = CADANS_WINDOW_MS[bucket]
  if (window <= 0) return true
  const last = lastBucketAt.get(bucket) ?? 0
  const t = now()
  if (t - last < window) return false
  lastBucketAt.set(bucket, t)
  return true
}

// ── Intent → synth mapping (the SSOT) ────────────────────────
function dispatch(intent: SoundIntent, opts?: SoundIntentOptions): void {
  switch (intent) {
    case 'answer.correct':
      soundManager.playCorrect(opts?.comboLevel ?? 0)
      return
    case 'answer.wrong':
      soundManager.playWrong()
      return
    case 'reward.levelUp':
      soundManager.playLevelUp()
      return
    case 'reward.streak':
      soundManager.playStreak()
      return
    case 'reward.streakMilestone':
      soundManager.playStreakMilestone()
      return
    case 'reward.achievement':
      soundManager.playAchievement()
      return
    case 'reward.goalReached':
      soundManager.playGoalReached()
      return
    case 'nav.move':
      soundManager.playNavigate()
      return
    case 'nav.click':
      soundManager.playClick()
      return
    case 'mascot.speak':
      soundManager.playDjaySpeak()
      return
    case 'counter.tick':
      soundManager.playTick()
      return
    case 'counter.sweep':
      soundManager.playXpSweep(opts?.durationMs)
      return
    case 'system.block':
      soundManager.playError()
      return
  }
}

/**
 * Play the sound for a given moment. Callers say WHAT happened; the layer
 * decides WHICH sound and whether the category's cadans allows it right now.
 */
export function playSoundIntent(
  intent: SoundIntent,
  opts?: SoundIntentOptions,
): void {
  if (!passesCadans(INTENT_BUCKET[intent])) return
  dispatch(intent, opts)
}
